import {
  FileText, Users, CalendarDays, Receipt, Pill, BarChart3, Bell,
  MessageSquare, ShieldCheck, Stethoscope, HeartPulse,
  BookOpen, LifeBuoy, Code2, PlayCircle, GraduationCap, Newspaper,
  Building2, Briefcase,
} from "lucide-react";

export type MenuItem = {
  icon: React.ElementType;
  title: string;
  desc: string;
  href: string;
  badge?: string;
  /** App route the "Open in app" CTA should point to on the feature page. */
  appHref?: string;
};
export type MenuGroup = { heading: string; items: MenuItem[] };
export type MenuPanel = {
  key: string;
  label: string;
  columns: MenuGroup[];
  featured?: { title: string; desc: string; href: string; cta: string };
};

export const PANELS: MenuPanel[] = [
  {
    key: "product",
    label: "Product",
    columns: [
      {
        heading: "Clinical",
        items: [
          { icon: FileText, title: "Prescriptions", desc: "AI-assisted Rx with templates", href: "/features/prescriptions", appHref: "/prescriptions", badge: "New" },
          { icon: Pill, title: "Medicine Library", desc: "50+ BD brands, autocomplete", href: "/features/medicine-library", appHref: "/medicines" },
          { icon: Stethoscope, title: "Clinical Guidelines", desc: "WHO & DGHS protocols", href: "/features/clinical-guidelines", appHref: "/clinical-guidelines" },
          { icon: HeartPulse, title: "Vitals & Risk", desc: "Trends and AI risk score", href: "/features/vitals-risk", appHref: "/patients" },
        ],
      },
      {
        heading: "Practice",
        items: [
          { icon: Users, title: "Patients", desc: "One profile, complete history", href: "/features/patients", appHref: "/patients" },
          { icon: CalendarDays, title: "Appointments", desc: "Queue, kiosk, reminders", href: "/features/appointments", appHref: "/appointments" },
          { icon: Receipt, title: "Billing", desc: "BDT invoices, bKash & Nagad", href: "/features/billing", appHref: "/billing" },
          { icon: Building2, title: "Chambers", desc: "Multi-location scheduling", href: "/features/chambers", appHref: "/chambers" },
        ],
      },
      {
        heading: "Insights",
        items: [
          { icon: BarChart3, title: "Analytics", desc: "Revenue & practice KPIs", href: "/features/analytics", appHref: "/analytics" },
          { icon: Bell, title: "Notifications", desc: "Realtime alerts & recalls", href: "/features/notifications", appHref: "/recalls" },
          { icon: MessageSquare, title: "Messaging", desc: "SMS, WhatsApp templates", href: "/features/messaging", appHref: "/communications" },
          { icon: ShieldCheck, title: "Compliance", desc: "RLS, audit trail, encrypted", href: "/features/compliance", appHref: "/settings" },
        ],
      },
    ],
    featured: {
      title: "See DoctorsX in 2 minutes",
      desc: "Watch a real BD practice run a full clinic day.",
      href: "/app",
      cta: "Open dashboard",
    },
  },
  {
    key: "solutions",
    label: "Solutions",
    columns: [
      {
        heading: "By practice",
        items: [
          { icon: Stethoscope, title: "Solo doctors", desc: "Everything you need day one", href: "/solutions/solo-doctors", appHref: "/app" },
          { icon: Building2, title: "Multi-chamber", desc: "Consistency across locations", href: "/solutions/multi-chamber", appHref: "/chambers" },
          { icon: Briefcase, title: "Doctors with an assistant", desc: "Delegate front-desk to your assistant", href: "/solutions/clinics-hospitals", appHref: "/staff" },
        ],
      },
      {
        heading: "By specialty",
        items: [
          { icon: HeartPulse, title: "Cardiology", desc: "Vitals-first workflows", href: "/solutions/cardiology", appHref: "/patients" },
          { icon: Users, title: "Pediatrics", desc: "BD EPI vaccine schedule", href: "/solutions/pediatrics", appHref: "/vaccinations" },
          { icon: Pill, title: "General practice", desc: "Fast Rx & follow-ups", href: "/solutions/general-practice", appHref: "/prescriptions" },
        ],
      },
    ],
    featured: {
      title: "Built for Bangladesh",
      desc: "Bilingual, BMDC-ready, BDT-native.",
      href: "/about",
      cta: "See why",
    },
  },
  {
    key: "resources",
    label: "Resources",
    columns: [
      {
        heading: "Learn",
        items: [
          { icon: BookOpen, title: "Documentation", desc: "Guides and how-tos", href: "/resources/documentation" },
          { icon: GraduationCap, title: "Academy", desc: "Onboarding courses", href: "/resources/academy" },
          { icon: PlayCircle, title: "Video library", desc: "Watch feature walkthroughs", href: "/resources/video-library" },
        ],
      },
      {
        heading: "Community",
        items: [
          { icon: Newspaper, title: "Blog", desc: "Product & clinical updates", href: "/resources/blog" },
          { icon: LifeBuoy, title: "Support", desc: "24/7 help in Bangla", href: "/resources/support" },
          { icon: Code2, title: "API & Integrations", desc: "Extend your workflow", href: "/resources/api-integrations" },
        ],
      },
    ],
    featured: {
      title: "What's new",
      desc: "AI diagnosis suggester now live.",
      href: "/resources/blog",
      cta: "Read release notes",
    },
  },
];

export type FeatureContent = {
  section: "features" | "solutions" | "resources";
  slug: string;
  eyebrow: string;
  title: string;
  tagline: string;
  intro: string;
  highlights: { title: string; desc: string }[];
  useCases?: string[];
  cta: { primaryLabel: string; primaryHref: string; secondaryLabel?: string; secondaryHref?: string };
};

const build = (
  section: FeatureContent["section"],
  slug: string,
  eyebrow: string,
  title: string,
  tagline: string,
  intro: string,
  highlights: FeatureContent["highlights"],
  useCases: string[],
  appHref?: string,
): FeatureContent => ({
  section, slug, eyebrow, title, tagline, intro, highlights, useCases,
  cta: appHref
    ? { primaryLabel: "Start free", primaryHref: "/auth", secondaryLabel: "Talk with us", secondaryHref: "/about" }
    : { primaryLabel: "Start free", primaryHref: "/auth", secondaryLabel: "Talk with us", secondaryHref: "/about" },
});

const F = (slug: string, ...args: [string, string, string, string, FeatureContent["highlights"], string[], string?]) =>
  build("features", slug, ...args);
const S = (slug: string, ...args: [string, string, string, string, FeatureContent["highlights"], string[], string?]) =>
  build("solutions", slug, ...args);
const R = (slug: string, ...args: [string, string, string, string, FeatureContent["highlights"], string[], string?]) =>
  build("resources", slug, ...args);

const list: FeatureContent[] = [
  // ---------- FEATURES ----------
  F("prescriptions", "Clinical", "Prescriptions",
    "AI-assisted Rx built for Bangladeshi practice.",
    "Compose safe, legible prescriptions in seconds — with BD brands, 1+0+1 dose autocomplete, digital signatures, and handwriting-style Rx headers.",
    [
      { title: "AI diagnosis suggester", desc: "Get differential suggestions from symptoms — you stay in control." },
      { title: "Reusable templates", desc: "Save common Rx patterns per specialty and reuse in one click." },
      { title: "Digital signature", desc: "BMDC-ready signatures printed on branded prescription pads." },
      { title: "Bilingual output", desc: "English + Bangla instructions patients actually understand." },
    ],
    ["High-volume OPDs", "Rural telemedicine", "Follow-up refills"],
    "/prescriptions"),

  F("medicine-library", "Clinical", "Medicine Library",
    "50+ Bangladeshi brands, always up to date.",
    "Napa, Telsar, Rivotril and hundreds more — with strength, form, department badges, and smart defaults built in.",
    [
      { title: "Autocomplete-first", desc: "Type two letters and pick the right brand and strength." },
      { title: "Department badges", desc: "See the therapeutic area at a glance while prescribing." },
      { title: "Interaction hints", desc: "Warnings surface before you sign the Rx." },
      { title: "Custom favourites", desc: "Pin your top 20 to the top of every search." },
    ],
    ["Solo GPs", "Multi-doctor clinics", "Pharmacy handover"],
    "/medicines"),

  F("clinical-guidelines", "Clinical", "Clinical Guidelines",
    "WHO and DGHS protocols one click away.",
    "Reference vetted clinical pathways without leaving the patient chart — from BD EPI to hypertension staging.",
    [
      { title: "Curated for BD", desc: "Localised to DGHS recommendations and BD drug availability." },
      { title: "Inline reference", desc: "Open guidelines side-by-side with the chart." },
      { title: "Search by symptom", desc: "Type 'chest pain' and jump to the relevant pathway." },
    ],
    ["Junior residents", "GP refresher", "Emergency triage"],
    "/clinical-guidelines"),

  F("vitals-risk", "Clinical", "Vitals & Risk Score",
    "See the trend before it becomes a problem.",
    "Every visit's BP, pulse, SpO₂, and weight roll into a rich trendline, plus a 1–100 AI risk score that flags patients who need attention.",
    [
      { title: "Trend visualisation", desc: "Sparkline per vital, right on the patient header." },
      { title: "AI risk score", desc: "Bandaid model prioritises whom to recall this week." },
      { title: "Auto flags", desc: "Missed follow-ups, sudden vital drops, medication gaps." },
    ],
    ["Chronic care", "Post-op recovery", "Diabetic panels"],
    "/patients"),

  F("patients", "Practice", "Patient Records",
    "One profile. Every visit. Zero paper.",
    "Every prescription, invoice, note, vaccine and lab in one always-searchable record.",
    [
      { title: "Unified timeline", desc: "See consultations, Rx, payments and vitals on one page." },
      { title: "Fast intake", desc: "Register a walk-in in under 20 seconds with Cmd+Shift+N." },
      { title: "Family linking", desc: "Group siblings, parents and dependants under one household." },
    ],
    ["Family practice", "Chronic care", "Referral inbox"],
    "/patients"),

  F("appointments", "Practice", "Appointments & Queue",
    "Book, check-in and call — without the chaos.",
    "Scheduled slots, walk-ins and the waiting queue live in one calm view. Kiosk mode, 3-tone chime and 8-minute wait estimates included.",
    [
      { title: "Kiosk check-in", desc: "Patients tap their phone number, staff stay focused." },
      { title: "Realtime queue", desc: "Auto-updates for every screen in the chamber." },
      { title: "Smart reminders", desc: "SMS/WhatsApp 24h before and morning of." },
    ],
    ["Busy OPDs", "Multi-doctor days", "Kiosk-driven clinics"],
    "/appointments"),

  F("billing", "Practice", "Billing & Invoicing",
    "BDT-native billing with bKash and Nagad, done.",
    "Charge fees per chamber, mark Paid / Partial / Unpaid, and hand patients a bilingual PDF invoice on the spot.",
    [
      { title: "Multi-chamber fees", desc: "Different visit fees per location, remembered automatically." },
      { title: "Payment methods", desc: "Cash, Card, bKash, Nagad — with transaction refs." },
      { title: "Bilingual PDFs", desc: "Print or WhatsApp the invoice in English + Bangla." },
    ],
    ["Consultation fees", "Follow-up billing", "Package pricing"],
    "/billing"),

  F("chambers", "Practice", "Chambers & Locations",
    "Run every chamber with one calendar.",
    "Configure hours, breaks, notice windows and fees per location — patients only see slots you can actually keep.",
    [
      { title: "Multiple break windows", desc: "Model a real BD schedule, not a rigid grid." },
      { title: "Booking rules", desc: "Min notice + max advance enforced at the API layer." },
      { title: "Public profile", desc: "Each chamber can carry its own branded booking page." },
    ],
    ["Multi-location doctors", "Weekend chambers", "Visiting consultants"],
    "/chambers"),

  F("analytics", "Insights", "Practice Analytics",
    "Know your day, week and month at a glance.",
    "Revenue, visit mix, top diagnoses, and per-chamber KPIs — with the হিসাব খাতা daily income register.",
    [
      { title: "Daily income register", desc: "Traditional হিসাব খাতা, digital and shareable." },
      { title: "Payer breakdown", desc: "See cash vs bKash vs Nagad share instantly." },
      { title: "Recall opportunities", desc: "Analytics that turn into action items." },
    ],
    ["Owner-doctors", "Practice managers", "Accountants"],
    "/analytics"),

  F("notifications", "Insights", "Notifications & Recalls",
    "Never lose a patient to a missed follow-up.",
    "Realtime alerts for new bookings, feedback and dues — plus a proactive recall list of patients overdue for follow-up.",
    [
      { title: "Realtime bell", desc: "Live from the database, no polling, no delay." },
      { title: "Recall lists", desc: "Auto-generated from Rx follow-up dates and vitals." },
      { title: "Bulk mark-read", desc: "Clear the tray in a single keystroke." },
    ],
    ["Chronic care", "Post-op check-ins", "Vaccine boosters"],
    "/recalls"),

  F("messaging", "Insights", "Messaging Templates",
    "SMS and WhatsApp, on-brand and on-time.",
    "Handlebars-style variables, Twilio integration and safe WhatsApp fallback links when a gateway is missing.",
    [
      { title: "Reusable templates", desc: "Appointment, invoice, follow-up, feedback — pre-built." },
      { title: "Variable merge", desc: "{{patient_name}} {{amount}} {{date}} — filled at send time." },
      { title: "WhatsApp fallback", desc: "Pre-filled wa.me links when Twilio isn't configured." },
    ],
    ["Reminders", "Payment nudges", "Feedback requests"],
    "/communications"),

  F("compliance", "Insights", "Compliance & Security",
    "Row-level security, audit trail, encrypted at rest.",
    "Multi-tenant by design. Each doctor's data is isolated by RLS, backed by an immutable audit log.",
    [
      { title: "Row-level security", desc: "The database itself enforces access — not the client." },
      { title: "Audit trail", desc: "Every read and write, with actor and timestamp." },
      { title: "BMDC-aware", desc: "Registration numbers captured throughout." },
    ],
    ["Solo practices", "Group clinics", "Hospitals"],
    "/settings"),

  // ---------- SOLUTIONS ----------
  S("solo-doctors", "By practice", "Solo doctors",
    "Everything you need on day one.",
    "One doctor, one login, one calm dashboard. No IT team required.",
    [
      { title: "Zero setup", desc: "Pre-populated demo data lets you test in 2 minutes." },
      { title: "Fair pricing", desc: "One flat monthly fee — no per-Rx surprises." },
      { title: "Own your data", desc: "Export anytime, in a format your accountant loves." },
    ],
    ["General practice", "Homeopathy", "Consultants with 1 chamber"],
    "/app"),

  S("multi-chamber", "By practice", "Multi-chamber doctors",
    "Consistency across every location you visit.",
    "One patient record, one calendar, one invoice book — no matter which chamber the visit happened in.",
    [
      { title: "Per-chamber fees", desc: "Different pricing per location, patients see the right one." },
      { title: "Unified inbox", desc: "Bookings from every chamber land in one queue." },
      { title: "Location-aware analytics", desc: "Compare Dhanmondi vs Uttara revenue side-by-side." },
    ],
    ["Visiting consultants", "Weekend chambers", "Diagnostic-attached doctors"],
    "/chambers"),

  S("clinics-hospitals", "By practice", "Clinics & hospitals",
    "Team roles, permissions and shared queues.",
    "Bring your receptionists, junior doctors and accountants onto one system — with the right access each.",
    [
      { title: "Role-based access", desc: "Separate roles for admin, doctor and staff." },
      { title: "Shared queue", desc: "The receptionist checks in — the doctor calls the next." },
      { title: "Consolidated billing", desc: "One financial view across the whole clinic." },
    ],
    ["Polyclinics", "Small hospitals", "Diagnostic centres"],
    "/staff"),

  S("cardiology", "By specialty", "Cardiology",
    "Vitals-first workflows for heart specialists.",
    "BP, pulse, SpO₂ and ECG notes stay front-and-centre. AI risk scoring highlights the sickest patients.",
    [
      { title: "Trended vitals", desc: "See how BP moved across the last 12 visits, instantly." },
      { title: "Risk-tiered patient list", desc: "Sort your panel by AI risk, not just last visit." },
      { title: "Cardio Rx templates", desc: "Standard beta-blocker + statin ladders, one click." },
    ],
    ["Interventional cards", "Preventive cards", "Post-CABG follow-up"],
    "/patients"),

  S("pediatrics", "By specialty", "Pediatrics",
    "BD EPI vaccine schedule, built in.",
    "Track every child's growth, vaccines and follow-ups — with automatic recall for the next due dose.",
    [
      { title: "EPI schedule", desc: "The full Bangladesh immunisation calendar, per child." },
      { title: "Growth curves", desc: "Weight & height plotted on WHO reference charts." },
      { title: "Auto recalls", desc: "The next vaccine due date is on the dashboard, always." },
    ],
    ["Well-baby clinics", "School health", "Neonatal follow-up"],
    "/vaccinations"),

  S("general-practice", "By specialty", "General practice",
    "Fast Rx and follow-ups for high-volume days.",
    "Get 60 patients through the day without cutting corners. Autocomplete, templates and one-key follow-up scheduling.",
    [
      { title: "Under-60s Rx", desc: "Draft, sign and print a prescription in under 60 seconds." },
      { title: "Follow-up in one key", desc: "Cmd+F schedules the return visit and messages the patient." },
      { title: "Bilingual advice", desc: "Standard patient-education snippets in English + Bangla." },
    ],
    ["OPD", "Camp clinics", "Corporate health desks"],
    "/prescriptions"),

  // ---------- RESOURCES ----------
  R("documentation", "Learn", "Documentation",
    "Everything you need to run DoctorsX confidently.",
    "Step-by-step guides for every module, keyboard shortcut and workflow — searchable and always current.",
    [
      { title: "Module guides", desc: "One deep-dive per feature, with screenshots." },
      { title: "Keyboard reference", desc: "Every shortcut, printable one-pager." },
      { title: "Migration playbooks", desc: "Move from paper or another EMR without downtime." },
    ],
    ["New doctors", "Onboarding staff", "Trainers"]),

  R("academy", "Learn", "DoctorsX Academy",
    "Short courses to master the platform.",
    "Bite-sized video lessons that take you from install to power-user in under 90 minutes.",
    [
      { title: "Track-based", desc: "Separate tracks for doctors, staff and clinic owners." },
      { title: "Bangla + English", desc: "Every lesson subtitled in both languages." },
      { title: "Certificates", desc: "Earn a completion badge to share with your team." },
    ],
    ["Team training", "Onboarding", "Self-paced learning"]),

  R("video-library", "Learn", "Video library",
    "Watch every feature in action.",
    "Two-minute walkthroughs of every module, updated with each release.",
    [
      { title: "Feature-first", desc: "Find the exact clip that answers your question." },
      { title: "Release notes", desc: "Every new capability comes with a video." },
      { title: "Downloadable", desc: "Play offline in low-bandwidth chambers." },
    ],
    ["Quick reference", "Team meetings", "Self-training"]),

  R("blog", "Community", "Blog & release notes",
    "Product updates and clinical perspectives.",
    "What we shipped, why we shipped it, and how BD doctors are using it in the wild.",
    [
      { title: "Product updates", desc: "Feature launches with practical use cases." },
      { title: "Clinical essays", desc: "Guest posts from practising BD physicians." },
      { title: "Practice ops", desc: "Ideas for running a leaner, calmer clinic." },
    ],
    ["Curious doctors", "Practice managers", "Med students"]),

  R("support", "Community", "Support in Bangla",
    "Real humans, real Bangla, 24/7.",
    "Talk to someone who actually understands how a Dhanmondi chamber runs on a Friday evening.",
    [
      { title: "In-app chat", desc: "Median first response under 5 minutes during hours." },
      { title: "Phone & WhatsApp", desc: "For when you need a voice on the other end." },
      { title: "On-site onboarding", desc: "For clinics in Dhaka and Chittagong." },
    ],
    ["Solo doctors", "Clinic staff", "IT admins"]),

  R("api-integrations", "Community", "API & Integrations",
    "Extend DoctorsX into your existing stack.",
    "A clean REST API and webhooks to plug in labs, accounting and messaging systems.",
    [
      { title: "REST API", desc: "Read and write patients, appointments and invoices." },
      { title: "Webhooks", desc: "Get notified when a booking or payment happens." },
      { title: "Prebuilt integrations", desc: "Twilio, Meta WhatsApp, bKash, Nagad." },
    ],
    ["Diagnostic labs", "Accounting", "Third-party portals"]),
];

export const FEATURE_CONTENT: Record<string, FeatureContent> = Object.fromEntries(
  list.map((c) => [`${c.section}/${c.slug}`, c]),
);

/** Look up related items in the same panel (excluding current). */
export function relatedItemsFor(section: string, slug: string): MenuItem[] {
  const href = `/${section}/${slug}`;
  const panel = PANELS.find((p) => p.columns.some((c) => c.items.some((i) => i.href === href)));
  if (!panel) return [];
  const all = panel.columns.flatMap((c) => c.items);
  return all.filter((i) => i.href !== href).slice(0, 6);
}
